import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ScheduleCalendar } from '../../calendar';
import { between } from '@ui/style/style';
import { Link2, MagicStar } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import ButtonDropdown from '@ui/components/ButtonDropdown';
import { Icon } from '@iconify/react';
import { useTranslation } from 'react-i18next';
import NewEventDialogDrawer from '../EventsTab/NewEvent';
import { useParams } from 'react-router-dom';
import { useAppDispatch } from '@application/store/useStore';
import { useSelector } from 'react-redux';
import { getPartnerEventMonthlyAsync } from '@application/features/partnerEvent/partnerEventThunk';
import { hasPermission } from '@application/helpers/permission-helper';
const CalendarTab = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const [selectedOption, setSelectedOption] = useState('');
    const [newEventOpen, setNewEventOpen] = useState(false);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [currentView, setCurrentView] = useState('Month');
    const [lastYearFetched, setLastYearFetched] = useState(null);
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const { MonthlyPartnerEvent } = useSelector((state) => state.partnerEvent);
    const { selectedCompanyId } = useSelector((state) => state.company);
    // Permission checks
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Create');
    const canUpdatePartners = hasPermission(loggedInUserPermissions, 'Permissions.Partner.Update');
    useEffect(() => {
        if (id && selectedCompanyId && selectedCompanyId.length > 0 && currentView === 'Year') {
            const currentYear = currentDate.getFullYear();
            if (lastYearFetched !== currentYear) {
                dispatch(getPartnerEventMonthlyAsync({
                    partnerId: id,
                    month: 0,
                    year: currentYear
                }));
                setLastYearFetched(currentYear);
            }
        }
    }, [dispatch, id, currentView, currentDate.getFullYear(), selectedCompanyId, lastYearFetched]);
    // Handle Month/Week/Day views - make month-specific API calls
    useEffect(() => {
        if (id && selectedCompanyId && selectedCompanyId.length > 0 && currentView !== 'Year') {
            dispatch(getPartnerEventMonthlyAsync({
                partnerId: id,
                month: currentDate.getMonth() + 1,
                year: currentDate.getFullYear()
            }));
            if (currentView !== 'Year') {
                setLastYearFetched(null);
            }
        }
    }, [dispatch, id, currentDate.getMonth(), currentDate.getFullYear(), selectedCompanyId, currentView]);
    const calendarItems = useMemo(() => {
        if (!MonthlyPartnerEvent?.days) {
            return [];
        }
        const items = [];
        const isYearlyRequest = MonthlyPartnerEvent.month === 0;
        MonthlyPartnerEvent.days.forEach((day) => {
            // Create a date for this specific day
            let dayDate;
            if (isYearlyRequest) {
                dayDate = new Date(MonthlyPartnerEvent.year, 0, day.day);
            }
            else {
                dayDate = new Date(MonthlyPartnerEvent.year, MonthlyPartnerEvent.month - 1, // month is 1-based in backend, 0-based in JS
                day.day);
            }
            day.events.forEach((event) => {
                // Create calendar item for this event on this specific day
                items.push({
                    id: `${event.eventId}-${day.day}`,
                    title: event.title,
                    start: dayDate,
                    end: dayDate,
                    startTime: event.startTime,
                    type: 'event',
                    status: event.status === 'approved' ? "approved" : "pending",
                    eventId: event.eventId
                });
            });
        });
        return items;
    }, [MonthlyPartnerEvent, id, selectedCompanyId]);
    const dropdownOptions = [
        {
            id: 'link-api',
            text: 'Link an API',
            icon: _jsx(Link2, { variant: 'Outline' }),
            to: () => console.log('Link an API clicked'),
            group: true
        },
        {
            id: 'sky-sports',
            text: 'Sky sports',
            icon: _jsx(Icon, { icon: 'eva:checkmark-circle-fill' }),
            to: () => console.log('Sky sports clicked'),
            group: true,
            endIcon: (_jsx(Icon, { icon: 'eva:arrow-ios-forward-fill', width: '24', height: '24', style: { color: theme.palette.common.white } }))
        },
        {
            id: 'bands-in-town',
            text: 'Bands in town',
            icon: _jsx(Icon, { icon: 'eva:checkmark-fill' }),
            to: () => console.log('Bands in town clicked'),
            endIcon: (_jsx(Icon, { icon: 'eva:arrow-ios-forward-fill', width: '24', height: '24', style: { color: theme.palette.common.white } })),
            group: true
        },
        {
            id: 'song-kick',
            text: 'Song kick',
            icon: _jsx(Icon, { icon: 'eva:checkmark-fill' }),
            to: () => console.log('Song kick clicked'),
            endIcon: (_jsx(Icon, { icon: 'eva:arrow-ios-forward-fill', width: '24', height: '24', style: { color: theme.palette.common.white } })),
            group: true
        }
    ];
    return (_jsxs(Box, { display: 'flex', flexDirection: 'column', gap: theme.spacing(1.5), children: [_jsxs(Box, { sx: { ...between }, children: [_jsx(Typography, { variant: 'h6', children: t('sponsorships.calendar') }), _jsxs(Box, { display: 'flex', gap: '12px', children: [_jsx(ButtonDropdown, { label: 'Link an API', startIcon: _jsx(Link2, { variant: 'Outline' }), options: dropdownOptions, selectedItem: selectedOption, onSelectionChange: setSelectedOption, variant: 'outlined', color: 'inherit', size: isSmallScreen ? 'small' : 'large' }), _jsx(Button, { startIcon: _jsx(MagicStar, { variant: 'Outline' }), variant: 'outlined', color: 'primary', size: isSmallScreen ? 'small' : 'large', disabled: !canCreateEvents || !canUpdatePartners, onClick: () => setNewEventOpen(true), children: t('common.addEvent') })] })] }), _jsx(ScheduleCalendar, { items: calendarItems, onDateChange: setCurrentDate, onViewChange: setCurrentView }), _jsx(NewEventDialogDrawer, { open: newEventOpen, onClose: () => setNewEventOpen(false) })] }));
};
export default CalendarTab;
